import { useCallback } from 'react';
import { useElementRef, popoverRectForPosition, nudgePopoverRectIfNeeded } from './utils';
export const getInitialPopoverStyles = (canScrollOutOfView = true, zIndex) => ({
  position: canScrollOutOfView ? 'absolute' : 'fixed',
  overflow: 'visible',
  top: '0px',
  left: '0px',
  zIndex: zIndex || '9999'
});
export const usePopover = ({
  isOpen,
  placement,
  padding,
  boundaryElement,
  childRef,
  onPositionPopover,
  boundaryInset,
  canScrollOutOfView,
  zIndex
}) => {
  const popoverRef = useElementRef(getInitialPopoverStyles(canScrollOutOfView, zIndex));
  const positionPopover = useCallback(({
    popoverPosition = placement,
    childRect,
    popoverRect,
    boundaryRect
  }) => {
    if (!childRect && childRef.current) {
      childRect = childRef.current.getBoundingClientRect();
    }
    if (!popoverRect && popoverRef.current) {
      popoverRect = popoverRef.current.getBoundingClientRect();
    }
    if (!boundaryRect) {
      if (boundaryElement) {
        boundaryRect = boundaryElement.getBoundingClientRect();
      } else {
        boundaryRect = {
          top: 0,
          bottom: document.documentElement.clientHeight,
          left: 0,
          right: document.documentElement.clientWidth,
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight
        };
      }
    }
    if (!isOpen || !popoverRect || !childRect || !boundaryRect) return;
    const newPopoverRect = popoverRectForPosition(popoverPosition, childRect, popoverRect, padding);
    const nudgedRect = nudgePopoverRectIfNeeded({
      popoverRect: newPopoverRect,
      boundaryRect,
      boundaryInset
    });
    let {
      top
    } = nudgedRect;
    const {
      left,
      width,
      height
    } = nudgedRect;
    if (canScrollOutOfView) {
      top = newPopoverRect.top;
    }

    // When the global-toolbar and banners above it are set to be sticky, the popovers within the global-toolbar can
    // get displaced by the combined height of banners rendered outside the #hs-nav-v4 container.
    // This adjustment prevents any unwanted spacing between the popover trigger inside the toolbar and the popover itself.
    const isGlobalToolbarSticky = document.querySelector('body').classList.contains('sticky-global-toolbar');
    const isPopoverTriggerInGlobalToolbar = childRef.current.id.includes('hs-global-toolbar');
    if (isGlobalToolbarSticky && isPopoverTriggerInGlobalToolbar) {
      const growthDynamicContainer = document.querySelector(`div#growth-dynamic-ui`);
      const growthDynamicContainerHeight = growthDynamicContainer ? growthDynamicContainer.getBoundingClientRect().height : 0;
      const navBeforeContainer = document.querySelector(`div#hs-nav-before-container`);
      const navBeforeContainerHeight = navBeforeContainer ? navBeforeContainer.getBoundingClientRect().height : 0;
      const notificationBannerContainer = document.querySelector(`iframe#notifications-banner-iframe`);
      const notificationBannerContainerHeight = notificationBannerContainer ? notificationBannerContainer.getBoundingClientRect().height : 0;
      const onboardingToursBannerContainer = document.getElementById('onboarding-tours-banner');
      const onboardingToursBannerContainerHeight = onboardingToursBannerContainer ? onboardingToursBannerContainer.getBoundingClientRect().height : 0;
      top = top - (growthDynamicContainerHeight + navBeforeContainerHeight + notificationBannerContainerHeight + onboardingToursBannerContainerHeight);
    }
    popoverRef.current.style.transform = `translate(${left}px, ${top}px)`;
    onPositionPopover({
      childRect,
      popoverRect: {
        top,
        left,
        width,
        height,
        right: left + width,
        bottom: top + height
      },
      boundaryRect,
      placement: popoverPosition,
      padding,
      boundaryInset
    });
  }, [placement, childRef, popoverRef, isOpen, padding, boundaryInset, canScrollOutOfView, onPositionPopover, boundaryElement]);
  return {
    positionPopover,
    popoverRef
  };
};